import React, { useState, useEffect } from 'react';
import { 
  Home, 
  MessageCircle, 
  BookOpen, 
  ShoppingBag, 
  User,
  Menu,
  X,
  Heart,
  Lock,
  Eye,
  LogOut,
  Bell
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';

interface NavigationProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  isMobileMenuOpen: boolean;
  onMobileMenuToggle: () => void;
  currentUser: any | null;
  setShowAuthModal: (show: boolean) => void;
  setAuthMode: (mode: 'signin' | 'signup') => void;
}

const Navigation: React.FC<NavigationProps> = ({
  activeSection,
  onSectionChange,
  isMobileMenuOpen,
  onMobileMenuToggle,
  currentUser,
  setShowAuthModal,
  setAuthMode
}) => {
  const { signOut } = useAuth();
  const { unreadCount } = useNotifications();

  // Define navigation items with proper access control
  const navItems = [
    // Public sections (always accessible)
    { 
      id: 'discover', 
      label: 'Discover', 
      icon: BookOpen, 
      description: 'Health articles & drug info',
      requiresAuth: false
    },
    { 
      id: 'community', 
      label: 'Community', 
      icon: MessageCircle, 
      description: 'Connect & discuss',
      requiresAuth: false
    },
    { 
      id: 'spotlight', 
      label: 'Expert Directory', 
      icon: User, 
      description: 'Find health experts',
      requiresAuth: false
    },
    { 
      id: 'shop', 
      label: 'Shop', 
      icon: ShoppingBag, 
      description: 'Health products',
      requiresAuth: false
    },
    
    // Private sections (require authentication)
    { 
      id: 'hub', 
      label: 'Hub', 
      icon: Home, 
      description: 'Your health dashboard',
      requiresAuth: true
    },
    { 
      id: 'profile', 
      label: 'Profile', 
      icon: User, 
      description: 'Your account & settings',
      requiresAuth: true,
      badge: unreadCount > 0 ? unreadCount : undefined
    }
  ];

  const handleNavClick = (item: any) => {
    // Check if section requires authentication
    if (item.requiresAuth && !currentUser) {
      // Show auth modal for private sections
      setAuthMode('signin');
      setShowAuthModal(true);
      return;
    }
    
    // Navigate to section
    onSectionChange(item.id);
  };

  const handleSignOut = async () => {
    await signOut();
    // Redirect to discover page after sign out
    onSectionChange('discover');
  };

  return (
    <>
      {/* Mobile Bottom Navigation */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 glass border-t border-gray-200 dark:bg-gray-900/90 dark:border-gray-800 z-50 safe-bottom">
        <div className="grid grid-cols-5 py-2">
          {navItems.slice(0, 5).map((item, index) => {
            const Icon = item.icon;
            const isLocked = item.requiresAuth && !currentUser;
            
            return (
              <button
                key={item.id}
                onClick={() => handleNavClick(item)}
                className={`flex flex-col items-center py-2 px-1 transition-all duration-300 group relative animate-fade-in ${
                  activeSection === item.id
                    ? 'text-green-600 dark:text-green-400'
                    : isLocked 
                    ? 'text-gray-400 dark:text-gray-500'
                    : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                }`}
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className={`p-2 rounded-xl transition-all duration-300 relative ${
                  activeSection === item.id
                    ? 'bg-green-100 dark:bg-green-900/30 scale-110 shadow-lg'
                    : isLocked
                    ? 'bg-gray-50 dark:bg-gray-800'
                    : 'hover:bg-gray-100 dark:hover:bg-gray-800 group-hover:scale-105'
                }`}>
                  <Icon size={20} className="transition-transform duration-200" />
                  {isLocked && (
                    <Lock size={10} className="absolute -top-1 -right-1 text-gray-400 dark:text-gray-500" />
                  )}
                  {item.badge && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </div>
                <span className={`text-xs font-medium mt-1 transition-all duration-200 ${
                  activeSection === item.id ? 'scale-105' : ''
                }`}>
                  {item.label}
                </span>
                {activeSection === item.id && (
                  <div className="absolute -top-1 w-1 h-1 bg-green-600 dark:bg-green-400 rounded-full animate-pulse" />
                )}
              </button>
            );
          })}
        </div>
        
        {/* Authentication buttons for mobile */}
        {!currentUser && (
          <div className="px-4 pb-2 pt-1 border-t border-gray-100 dark:border-gray-800">
            <div className="flex space-x-2">
              <button
                onClick={() => {
                  setAuthMode('signin');
                  setShowAuthModal(true);
                }}
                className="btn-primary btn-sm flex-1"
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  setAuthMode('signup');
                  setShowAuthModal(true);
                }}
                className="btn-secondary btn-sm flex-1"
              >
                Sign Up
              </button>
            </div>
          </div>
        )}

        {/* User menu for mobile when authenticated */}
        {currentUser && (
          <div className="px-4 pb-2 pt-1 border-t border-gray-100 dark:border-gray-800">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <div className="avatar-sm">
                  {currentUser.user_metadata?.full_name?.charAt(0) || currentUser.email?.charAt(0) || 'U'}
                </div>
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {currentUser.user_metadata?.full_name || 'User'}
                </span>
              </div>
              <button
                onClick={handleSignOut}
                className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                <LogOut size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex fixed left-0 top-0 h-full w-72 glass border-r border-gray-200 dark:bg-gray-900/90 dark:border-gray-800 shadow-xl z-40 flex-col animate-slide-up">
        {/* Header */}
        <div className="card-padding-md border-b border-gray-200 dark:border-gray-800">
          <button 
            onClick={() => onSectionChange('landing')}
            className="flex items-center space-x-3 group cursor-pointer w-full text-left"
          >
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-blue-600 dark:from-green-600 dark:to-blue-700 rounded-xl flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-3">
              <Heart className="text-white transition-transform duration-300 group-hover:scale-110" size={24} />
            </div>
            <div className="transition-all duration-300 group-hover:translate-x-1">
              <h1 className="text-xl font-bold gradient-text">
                PharmaPlus
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 transition-colors duration-200 group-hover:text-gray-800 dark:group-hover:text-gray-300">
                Health Community
              </p>
            </div>
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto scrollbar-custom">
          <ul className="space-y-2">
            {navItems.map((item, index) => {
              const Icon = item.icon;
              const isLocked = item.requiresAuth && !currentUser;
              
              return (
                <li key={item.id} className="animate-slide-up" style={{ animationDelay: `${index * 100}ms` }}>
                  <button
                    onClick={() => handleNavClick(item)}
                    className={`w-full flex items-center space-x-3 p-3 rounded-xl text-left transition-all duration-300 group relative overflow-hidden ${
                      activeSection === item.id
                        ? 'bg-gradient-to-r from-green-500 to-blue-500 dark:from-green-600 dark:to-blue-600 text-white shadow-lg scale-105'
                        : isLocked
                        ? 'text-gray-400 dark:text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100 hover:scale-102'
                    }`}
                  >
                    {/* Background animation for active state */}
                    {activeSection === item.id && (
                      <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-400 dark:from-green-500 dark:to-blue-500 opacity-20 animate-pulse" />
                    )}
                    
                    <div className={`p-2 rounded-lg transition-all duration-300 relative z-10 ${
                      activeSection === item.id
                        ? 'bg-white/20 shadow-inner'
                        : isLocked
                        ? 'bg-gray-100 dark:bg-gray-800'
                        : 'bg-gray-100 dark:bg-gray-800 group-hover:bg-gray-200 dark:group-hover:bg-gray-700 group-hover:scale-110'
                    }`}>
                      <Icon size={18} className="transition-transform duration-300 group-hover:rotate-12" />
                      {isLocked && (
                        <Lock size={10} className="absolute -top-1 -right-1 text-gray-400 dark:text-gray-500" />
                      )}
                      {item.badge && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                          {item.badge > 9 ? '9+' : item.badge}
                        </span>
                      )}
                    </div>
                    <div className="flex-1 relative z-10">
                      <span className="font-medium block transition-all duration-200 group-hover:translate-x-1">
                        {item.label}
                      </span>
                      <span className={`text-xs transition-all duration-200 ${
                        activeSection === item.id ? 'text-white/80' : 
                        isLocked ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                      }`}>
                        {item.description}
                      </span>
                    </div>
                    
                    {/* Lock indicator for private sections */}
                    {isLocked && (
                      <div className="absolute right-2 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <span className="badge-base bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400">
                          Sign In
                        </span>
                      </div>
                    )}
                    
                    {/* Active indicator */}
                    {activeSection === item.id && (
                      <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-1 h-8 bg-white rounded-l-full shadow-lg" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Footer */}
        <div className="card-padding-md border-t border-gray-200 dark:border-gray-800">
          {!currentUser ? (
            <div className="space-y-3">
              <button
                onClick={() => {
                  setAuthMode('signin');
                  setShowAuthModal(true);
                }}
                className="btn-primary w-full"
              >
                Sign In
              </button>
              <button
                onClick={() => {
                  setAuthMode('signup');
                  setShowAuthModal(true);
                }}
                className="btn-secondary w-full"
              >
                Create Account
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="bg-gradient-to-r from-green-50 to-blue-50 dark:from-green-900/30 dark:to-blue-900/30 p-4 rounded-xl border border-green-200 dark:border-green-800 hover:shadow-lg transition-all duration-300 group cursor-pointer">
                <div className="flex items-center space-x-2 mb-2">
                  <div className="avatar-sm group-hover:scale-110 transition-transform duration-300">
                    <Heart className="text-white transition-transform duration-300 group-hover:scale-110" size={16} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-gray-100 text-sm transition-all duration-200 group-hover:translate-x-1">Health First</p>
                  </div>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed transition-colors duration-200 group-hover:text-gray-800 dark:group-hover:text-gray-300">
                  Your trusted health community
                </p>
              </div>
              
              <button
                onClick={handleSignOut}
                className="btn-ghost w-full flex items-center justify-center space-x-2"
              >
                <LogOut size={16} />
                <span>Sign Out</span>
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Navigation;